import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  CheckBadgeIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  PaintBrushIcon,
  PencilSquareIcon,
  PlusIcon,
  RocketLaunchIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BUILTIN_PRINT_TEMPLATES, paperLabel, parsePrintTemplateSpec, type PrintTemplateSpec } from 'shared-types';
import { Button } from '../../components/Button';
import { ListItem } from '../../components/ListItem';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { ConfirmDialog } from '../../components/Modal';
import { PageContent } from '../../components/page/PageContent';
import { PageHeader } from '../../components/page/PageHeader';
import { StatusPill } from '../../components/Pill';
import { DotsPopover } from '../../components/Popover/DotsPopover';
import { Tooltip } from '../../components/Tooltip';
import { orgPrintFontsOf } from '../../modules/print/fonts/orgFonts';
import { TemplateThumbnail } from '../../modules/print/preview/TemplateThumbnail';
import { usePreviewEnv } from '../../modules/print/preview/usePreviewEnv';
import { usePrintImages } from '../../modules/print/preview/usePrintImages';
import { useBuiltinTemplateText } from '../../modules/print/builtinTemplateText';
import { BrandKitModal } from '../../modules/templateLibrary/BrandKitModal';
import { configInputOf } from '../../modules/templateLibrary/configInput';
import { NewTemplateModal } from '../../modules/templateLibrary/NewTemplateModal';
import { useTemplateLibraryEvents } from '../../modules/templateLibrary/useTemplateLibraryEvents';
import { T, useT } from '../../translation/src';
import {
  PrintTemplateSource,
  PrintTemplateStatus,
  useOrganisationTemplateLibraryQuery,
  useOrganisationTemplateLibraryUpdateConfigMutation,
  usePrintTemplateCreateMutation,
  usePrintTemplateDeleteMutation,
  usePrintTemplatePublishMutation,
  usePrintTemplateSetArchivedMutation,
  type PrintTemplateFieldsFragment,
} from '../../types/graphqlTypes';

/** A card: an organisation template or a built-in. */
interface Entry {
  key: string;
  source: 'org' | 'builtin';
  id: string;
  name: string;
  description: string;
  spec: PrintTemplateSpec | null;
  template: PrintTemplateFieldsFragment | null;
  hidden: boolean;
  isDefault: boolean;
}

interface CardProps {
  entry: Entry;
  canEdit: boolean;
  thumbnail: React.ReactNode;
  onOpen: (entry: Entry) => void;
  onPublish: (entry: Entry) => void;
  onDuplicate: (entry: Entry) => void;
  onToggleHidden: (entry: Entry) => void;
  onMakeDefault: (entry: Entry) => void;
  onRemove: (entry: Entry) => void;
}

const TemplateCard_: React.FC<CardProps> = ({ entry, canEdit, thumbnail, onOpen, onPublish, onDuplicate, onToggleHidden, onMakeDefault, onRemove }) => {
  const hideLabel = useT('hide from projects', { swc: true });
  const showLabel = useT('show in projects', { swc: true });
  const { template } = entry;
  const isDraft = template?.status === PrintTemplateStatus.Draft;
  const page = entry.spec?.pages[0];
  const toggleLabel = entry.hidden ? showLabel : hideLabel;
  // Only built-ins are hidden (the organisation's own are archived instead).
  const hideable = canEdit && !template;
  return (
    <div
      onClick={() => canEdit && onOpen(entry)}
      className={classNames(
        'group relative flex flex-col rounded-xl border p-3 transition-shadow hover:shadow-md',
        template ? 'border-gray-200 bg-white dark:border-gray-700 dark:bg-eerie-black' : 'border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-[#2a2a2a]',
        canEdit && 'cursor-pointer',
      )}
    >
      <div className={classNames('h-40 rounded bg-gray-50 p-2 dark:bg-gray-900', entry.hidden && 'opacity-30')}>{thumbnail}</div>
      <div className={classNames('mt-2 flex items-center gap-1 truncate text-sm font-medium', entry.hidden && 'text-gray-400')} title={entry.name}>
        {entry.isDefault && <CheckBadgeIcon className="h-4 w-4 shrink-0 text-french-violet-600 dark:text-neon-green-300" />}
        <span className="truncate">{entry.name}</span>
      </div>
      <div className="mt-0.5 truncate text-xs text-gray-500">
        {page && (
          <>
            {paperLabel(page.paper)} · {entry.spec?.pages.length === 1 ? <T _str="1 page" /> : <T _str="{count} pages" args={{ count: entry.spec?.pages.length ?? 0 }} />}
          </>
        )}
      </div>
      <div className="mt-1 flex min-h-4 flex-wrap gap-1">
        {isDraft && (
          <StatusPill status="process" className="text-[11px] font-medium leading-none">
            <T _str="draft" swc />
          </StatusPill>
        )}
        {template && !isDraft && template.hasUnpublishedChanges && (
          <StatusPill status="warning" className="text-[11px] font-medium leading-none">
            <T _str="unpublished changes" swc />
          </StatusPill>
        )}
        {template && !isDraft && !template.hasUnpublishedChanges && (
          <StatusPill status="pending" className="text-[11px] font-medium leading-none">
            <T _str="published v{version}" args={{ version: template.version }} swc />
          </StatusPill>
        )}
      </div>
      {canEdit && (
        // The card's own controls: their clicks (the menu's too, though it renders elsewhere) never open the card.
        <div className="contents" onClick={(e) => e.stopPropagation()}>
          {hideable && (
            <Tooltip message={<span>{toggleLabel}</span>} wrapperClassName="absolute left-2 top-2 z-10">
              <button
                type="button"
                aria-label={toggleLabel}
                onClick={() => onToggleHidden(entry)}
                className={classNames('cursor-pointer rounded p-1 transition-opacity hover:bg-gray-100 dark:hover:bg-white/10', entry.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100')}
              >
                {entry.hidden ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </Tooltip>
          )}
          <DotsPopover anchor="bottom start" fitContent>
            <div className="flex flex-col">
              {template && (isDraft || template.hasUnpublishedChanges) && (
                <ListItem icon={RocketLaunchIcon} onClick={() => onPublish(entry)}>
                  {isDraft ? <T _str="publish" swc /> : <T _str="publish changes" swc />}
                </ListItem>
              )}
              {template && (
                <ListItem icon={PencilSquareIcon} onClick={() => onOpen(entry)}>
                  <T _str="edit" swc />
                </ListItem>
              )}
              <ListItem icon={DocumentDuplicateIcon} onClick={() => onDuplicate(entry)}>
                {template ? <T _str="duplicate" swc /> : <T _str="duplicate to customise" swc />}
              </ListItem>
              {!entry.isDefault && !entry.hidden && !isDraft && (
                <ListItem icon={CheckBadgeIcon} onClick={() => onMakeDefault(entry)}>
                  <T _str="use as default for new sheets" swc />
                </ListItem>
              )}
              {hideable && (
                <ListItem icon={entry.hidden ? EyeIcon : EyeSlashIcon} onClick={() => onToggleHidden(entry)}>
                  {toggleLabel}
                </ListItem>
              )}
              {template && (
                <ListItem icon={isDraft ? TrashIcon : ArchiveBoxIcon} onClick={() => onRemove(entry)} className="text-error-red-500">
                  {isDraft ? <T _str="delete" swc /> : <T _str="archive" swc />}
                </ListItem>
              )}
            </div>
          </DotsPopover>
        </div>
      )}
    </div>
  );
};
const TemplateCard = memo(TemplateCard_);

/**
 * The organisation's print templates: its own (draft → published, versioned)
 * and the built-ins, plus the brand kit every template draws from.
 */
export const TemplateLibrary: React.FC2 = () => {
  const { organisationId = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, refetch } = useOrganisationTemplateLibraryQuery({ variables: { organisationId, includeArchived: true }, skip: !organisationId });
  useTemplateLibraryEvents({ organisationId, onChanged: () => void refetch() });
  const library = data?.organisationTemplateLibrary;
  const canEdit = !!library?.canEdit;
  const env = usePreviewEnv(useMemo(() => orgPrintFontsOf(library?.config.fonts), [library?.config.fonts]));
  const images = usePrintImages(library?.config.logo?.url, []);
  const builtinText = useBuiltinTemplateText();
  const sources = useMemo(() => {
    const brand = library?.config.brand;
    return brand ? { org: { name: '', address: brand.address ?? '', phone: brand.phone ?? '', email: brand.email ?? '', website: brand.website ?? '', vat: brand.vat ?? '' } } : undefined;
  }, [library?.config.brand]);

  const [newOpen, setNewOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandSession, setBrandSession] = useState(0);
  const [removing, setRemoving] = useState<Entry | null>(null);
  /** A built-in opened for editing: confirm it is copied first (built-ins can't change). */
  const [copyingBuiltin, setCopyingBuiltin] = useState<Entry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [create] = usePrintTemplateCreateMutation({ refetchQueries: ['organisationTemplateLibrary'] });
  const [publish] = usePrintTemplatePublishMutation({ refetchQueries: ['organisationTemplateLibrary'] });
  const [setArchived] = usePrintTemplateSetArchivedMutation({ refetchQueries: ['organisationTemplateLibrary'] });
  const [deleteTemplate] = usePrintTemplateDeleteMutation({ refetchQueries: ['organisationTemplateLibrary'] });
  const [updateConfig] = useOrganisationTemplateLibraryUpdateConfigMutation({ refetchQueries: ['organisationTemplateLibrary'] });
  const messages = {
    publishFailed: useT('publishing failed: open the template to see what is wrong', { swc: true }),
    published: useT('published: new sheets use this version', { swc: true }),
    failed: useT('that did not work, try again', { swc: true }),
    copySuffix: useT('copy'),
    dismiss: useT('dismiss', { swc: true }),
  };

  const defaultRef = library?.config.defaultTemplate;
  const hidden = useMemo(() => new Set(library?.config.hiddenBuiltinIds ?? []), [library?.config.hiddenBuiltinIds]);

  const { own, archived, builtins } = useMemo(() => {
    const templates = library?.templates ?? [];
    const toEntry = (template: PrintTemplateFieldsFragment): Entry => {
      const parsed = parsePrintTemplateSpec(template.draftSpec ?? template.publishedSpec);
      return {
        key: `org:${template.id}`,
        source: 'org',
        id: template.id,
        name: template.name,
        description: template.description ?? '',
        spec: parsed.ok ? parsed.spec : null,
        template,
        hidden: false,
        isDefault: defaultRef?.source === PrintTemplateSource.Org && defaultRef.id === template.id,
      };
    };
    return {
      own: templates.filter((t) => t.status !== PrintTemplateStatus.Archived).map(toEntry),
      archived: templates.filter((t) => t.status === PrintTemplateStatus.Archived),
      builtins: BUILTIN_PRINT_TEMPLATES.map(
        (b): Entry => ({
          key: `builtin:${b.id}`,
          source: 'builtin',
          id: b.id,
          name: builtinText(b).name,
          description: builtinText(b).description,
          spec: b.spec,
          template: null,
          hidden: hidden.has(b.id),
          // Nothing chosen: the A3 plan sheet is what new sheets start from.
          isDefault: defaultRef ? defaultRef.source === PrintTemplateSource.Builtin && defaultRef.id === b.id : b.id === 'a3-plan',
        }),
      ),
    };
  }, [library?.templates, defaultRef, hidden, builtinText]);

  const openEditor = useCallback((templateId: string) => navigate(`/organisations/${organisationId}/templates/${templateId}/edit`), [navigate, organisationId]);

  const guarded = async (action: () => Promise<unknown>, failure = messages.failed) => {
    setError(null);
    try {
      await action();
    } catch {
      setError(failure);
    }
  };

  const duplicate = (entry: Entry) =>
    guarded(async () => {
      if (!entry.spec) return;
      const result = await create({
        variables: {
          organisationId,
          name: `${entry.name} ${messages.copySuffix}`.slice(0, 80),
          spec: entry.spec,
          basedOnBuiltinId: entry.source === 'builtin' ? entry.id : (entry.template?.basedOnBuiltinId ?? null),
          duplicateOfTemplateId: entry.source === 'org' ? entry.id : null,
        },
      });
      const id = result.data?.printTemplateCreate.id;
      if (id) openEditor(id);
    });

  const updateConfigWith = (patch: Partial<ReturnType<typeof configInputOf>>) =>
    guarded(async () => {
      if (!library) return;
      await updateConfig({ variables: { organisationId, config: { ...configInputOf(library.config), ...patch } } });
    });

  const toggleHidden = (entry: Entry) => {
    const next = entry.hidden ? [...hidden].filter((id) => id !== entry.id) : [...hidden, entry.id];
    // A hidden built-in can't stay the default.
    const keepDefault = !(defaultRef?.source === PrintTemplateSource.Builtin && defaultRef.id === entry.id && !entry.hidden);
    void updateConfigWith({ hiddenBuiltinIds: next, ...(keepDefault ? {} : { defaultTemplate: null }) });
  };

  const makeDefault = (entry: Entry) => void updateConfigWith({ defaultTemplate: { source: entry.source === 'org' ? PrintTemplateSource.Org : PrintTemplateSource.Builtin, id: entry.id } });

  const doPublish = (entry: Entry) =>
    guarded(async () => {
      await publish({ variables: { organisationId, templateId: entry.id } });
      toast.success(messages.published);
    }, messages.publishFailed);

  const remove = async () => {
    const entry = removing;
    if (!entry?.template) return;
    await guarded(() =>
      entry.template?.status === PrintTemplateStatus.Draft ? deleteTemplate({ variables: { organisationId, templateId: entry.id } }) : setArchived({ variables: { organisationId, templateId: entry.id, archived: true } }),
    );
    setRemoving(null);
  };

  const thumbnail = (entry: Entry) => (entry.spec ? <TemplateThumbnail env={env} spec={entry.spec} images={images} sources={sources} className="h-full w-full" /> : null);
  const removingIsDraft = removing?.template?.status === PrintTemplateStatus.Draft;

  const grid = (entries: Entry[]) => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
      {entries.map((entry) => (
        <TemplateCard
          key={entry.key}
          entry={entry}
          canEdit={canEdit}
          thumbnail={thumbnail(entry)}
          onOpen={(e) => (e.template ? openEditor(e.id) : setCopyingBuiltin(e))}
          onPublish={(e) => void doPublish(e)}
          onDuplicate={(e) => void duplicate(e)}
          onToggleHidden={toggleHidden}
          onMakeDefault={makeDefault}
          onRemove={setRemoving}
        />
      ))}
    </div>
  );

  return (
    <PageContent>
      <div className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={<T _str="print templates" swc />}
          button={
            canEdit ? (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => {
                    setBrandSession((n) => n + 1);
                    setBrandOpen(true);
                  }}
                >
                  <PaintBrushIcon className="mr-1 w-4" />
                  <T _str="brand kit" swc />
                </Button>
                <Button variant="primary" size="xs" onClick={() => setNewOpen(true)}>
                  <PlusIcon className="mr-1 w-4" />
                  <T _str="new template" swc />
                </Button>
              </div>
            ) : null
          }
        />
        {error && (
          <div role="alert" className="mb-4 flex items-center gap-3 rounded-lg border border-error-red-500/40 bg-error-red-500/10 px-4 py-2.5 text-sm">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-error-red-500" />
            <span className="flex-1">{error}</span>
            <button type="button" aria-label={messages.dismiss} onClick={() => setError(null)} className="rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto pb-10">
          {loading && !library ? (
            <EarthLoader />
          ) : (
            <div className="flex flex-col gap-8">
              <section>
                <h2 className="mb-3 flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  <T _str="your templates" />
                  <span className="font-normal text-gray-400">{own.length}</span>
                </h2>
                {own.length > 0 ? (
                  grid(own)
                ) : (
                  <p className="text-sm text-gray-500">
                    <T _str="no templates of your own yet: duplicate a built-in below or start a new one" swc />
                  </p>
                )}
              </section>
              <section>
                <h2 className="mb-3 flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  <T _str="built-in templates" />
                  <span className="font-normal text-gray-400">{builtins.length}</span>
                </h2>
                {grid(builtins)}
              </section>
              {archived.length > 0 && (
                <section className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <h2 className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
                    <T _str="archived" swc /> ({archived.length})
                  </h2>
                  <ul className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
                    {archived.map((template) => (
                      <li key={template.id} className="flex items-center justify-between py-2 text-sm">
                        <span>{template.name}</span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => void guarded(() => setArchived({ variables: { organisationId, templateId: template.id, archived: false } }))}
                            className="flex items-center gap-1 text-french-violet-600 hover:underline dark:text-neon-green-300"
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4" />
                            <T _str="restore" swc />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
      <NewTemplateModal open={newOpen} onClose={() => setNewOpen(false)} organisationId={organisationId} env={env} images={images} onCreated={openEditor} />
      {library && <BrandKitModal key={brandSession} open={brandOpen} onClose={() => setBrandOpen(false)} organisationId={organisationId} config={library.config} />}
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        variant={removingIsDraft ? 'danger' : 'warning'}
        title={removingIsDraft ? <T _str="delete this draft?" swc /> : <T _str="archive this template?" swc />}
        message={removingIsDraft ? <T _str="the draft was never published, so no sheet uses it" swc /> : <T _str="it leaves the template list; sheets already made with it keep exporting and it can be restored later" swc />}
        confirmLabel={removingIsDraft ? <T _str="delete" swc /> : <T _str="archive" swc />}
      />
      <ConfirmDialog
        open={!!copyingBuiltin}
        onClose={() => setCopyingBuiltin(null)}
        onConfirm={async () => {
          if (copyingBuiltin) await duplicate(copyingBuiltin);
        }}
        variant="info"
        title={<T _str="this is a built-in template" swc />}
        message={<T _str="built-in templates can't be changed. A copy is added to your library and opens in the editor." swc />}
        confirmLabel={<T _str="copy and edit" swc />}
      />
    </PageContent>
  );
};
